<template>
  <InputRow
    v-slot="{ validation: { valid } }"
    name="creatorFilter"
    :model-value="selected"
    required
  >
    <MultiSelect
      ref="filterRef"
      v-model="selected"
      track-by="id"
      option-label="displayName"
      :filter-fields="['name', 'displayName', 'id']"
      class="w-100"
      display="chip"
      auto-filter-focus
      filter
      :class="valid ? 'p-valid' : 'p-invalid'"
      :options="options"
      @filter="searchChange"
      @change="toggle"
    >
    </MultiSelect>
  </InputRow>
</template>

<script setup lang="ts">
import InputRow from "@/components/shared/form/InputRow.vue";
import MultiSelect from "primevue/multiselect";
import { computed, type PropType, ref } from "vue";
import CreatorFilter from "../filterImplementations/CreatorFilter";
import { FilterEntity } from "../FilterTypes";
import refilterEventsWithout from "./RefilterEventsWithout";
import { useStore } from "vuex";
import { hasRight } from "@/functions/authorizationState";
import { useProvidedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";

const props = defineProps({
  mode: {
    required: true,
    type: Number as PropType<FilterEntity>,
  },
});

const selected = ref<typeof options.value>([]);
const filteredEvents = refilterEventsWithout((filter) => !(filter instanceof CreatorFilter));
const store = useStore();
const { siteId } = useProvidedBoardContext();

const loggedInUserId = computed(() => store.getters["login/userId"]);
function checkHideResponsibleRight(id?: number) {
  const right = hasRight("hlp.hideResponsible", siteId.value);
  return (
    right ||
    id === loggedInUserId.value ||
    (selected.value.some((item) => item.id === id) && id !== loggedInUserId.value)
  );
}

const options = computed(() => {
  let people: Record<number, string>;
  switch (props.mode) {
    case FilterEntity.EVENT:
      people = filteredEvents.value.activeEvents.reduce(
        (prev, curr) => {
          if (
            curr.owner &&
            (curr.__typename !== "HLPIdea" || checkHideResponsibleRight(curr.owner.id))
          ) {
            prev[curr.owner.id] = curr.owner.preferredName;
          }

          return prev;
        },
        {} as Record<number, string>,
      );
      break;
    case FilterEntity.ACTION:
      people = filteredEvents.value.activeEvents
        .flatMap((event) => event.actions)
        .reduce(
          (prev, curr) => {
            if (curr.assignee) {
              prev[curr.assignee.id] = curr.assignee.preferredName;
            }
            return prev;
          },
          {} as Record<number, string>,
        );
      break;
    default:
      return [];
  }
  return Object.entries(people)
    .map(([id, name]) => ({
      id: Number(id),
      name: name.normalize("NFD").replace(/[\u0300-\u036f]/g, ""),
      displayName: name,
    }))
    .sort((lhs, rhs) => {
      return lhs.name.localeCompare(rhs.name);
    });
});

const filterRef = ref<InstanceType<typeof MultiSelect>>();

function searchChange(event: { originalEvent: Event; value: string }) {
  if (filterRef.value != null && "filterValue" in filterRef.value) {
    filterRef.value.filterValue = event.value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") as any;
  }
}
function toggle() {
  filterRef.value?.hide();
}

function getFilter() {
  return new CreatorFilter(selected.value.map(({ id }) => id));
}
function applyFilter(filter: CreatorFilter) {
  selected.value = filter.creators.map((id) => {
    const user = options.value.find((v) => v.id === id);
    return {
      id,
      name: user?.name ?? "?",
      displayName: user?.displayName ?? "?",
    };
  });
}

defineExpose({ getFilter, applyFilter });
</script>

<style lang="scss" scoped></style>
